import { api } from '@goaltree/backend/convex/_generated/api'
import { useAction, useQuery } from 'convex/react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const WARM_RETRY_MS = 60_000

// A spoken opener in the coach's voice for the Brain dump. Clips are cached server-side;
// one is preloaded ahead of time so `take()` can play instantly, or returns null (skip).
export function useBrainDumpPrompt(enabled: boolean) {
  const { i18n } = useTranslation()
  const data = useQuery(
    api.voiceIntro.brainDumpPrompts,
    enabled ? { locale: i18n.language } : 'skip',
  )
  const warm = useAction(api.voice.warmBrainDumpPrompts)
  const warmedAt = useRef(0)
  const next = useRef<HTMLAudioElement | null>(null)
  const urls = data?.urls ?? []
  const key = urls.join('|')

  useEffect(() => {
    if (!data || data.complete || Date.now() - warmedAt.current < WARM_RETRY_MS) return
    warmedAt.current = Date.now()
    warm({ locale: i18n.language }).catch(() => {})
  }, [data, i18n.language, warm])

  function preload() {
    if (!urls.length) {
      next.current = null
      return
    }
    const audio = new Audio(urls[Math.floor(Math.random() * urls.length)])
    audio.preload = 'auto'
    next.current = audio
  }

  useEffect(() => {
    preload()
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    take(): HTMLAudioElement | null {
      const audio = next.current
      if (!audio || audio.error) return null
      preload()
      return audio
    },
  }
}
